import React, { useState } from 'react';
import { SchoolMfaApi } from 'api/schoolMfaApi';
import ToggleSwitch from 'components/shared/ToggleSwitch';
import { useToastMessage } from 'hooks/useToast';
import { MfaRequestStatus } from 'pages/mfa/models';
import { SchoolMfaWarning } from './SchoolMfaWarning';

type Props = {
  status: MfaRequestStatus;
  onChanged: () => void;
};

export const SchoolMfaPanel: React.FC<Props> = ({ status, onChanged }) => {
  const [warningOpen, setWarningOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const showToast = useToastMessage();

  const requestChange = async (enable: boolean) => {
    setSubmitting(true);
    setError('');
    const result = await SchoolMfaApi.requestMfaChange(enable);
    setSubmitting(false);

    if (result.error) {
      setError(result.error.message ?? '');
      return false;
    }

    showToast(
      enable
        ? 'MFA is now on. Everyone at your school will need to verify again before opening pupil information.'
        : 'MFA is now off for everyone at your school.'
    );
    onChanged();
    return true;
  };

  const handleToggle = () => {
    if (submitting) return;
    if (status.bMfaEnabled) {
      setWarningOpen(true);
    } else {
      requestChange(true);
    }
  };

  return (
    <>
      <div className="SchoolMfaPanel">
        <div className="MfaPanelInner">
          <div className="LeftSide">
            <h2>School multi factor authentication</h2>
            <p>
              Set multi factor authentication (MFA) on or off across the entire
              school.
            </p>
            {error && <p className="MfaError">{error}</p>}
          </div>
          <div className="RightSide">
            <button
              type="button"
              className={
                'Btn MfaToggleBtn' +
                (status.bMfaEnabled ? ' BtnWhiteOrange' : ' BtnWhiteGrey')
              }
              onClick={handleToggle}
              disabled={submitting}
              aria-pressed={status.bMfaEnabled}
            >
              <span className="MtInner">
                <ToggleSwitch
                  onChange={() => {}}
                  value={status.bMfaEnabled}
                  readOnly
                />
                <span className="MtTxt">Multi factor authentication</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {warningOpen && (
        <SchoolMfaWarning
          isSubmitting={submitting}
          onConfirm={() => {
            // Closed first, so an MFA pop-up opened by the request isn't hidden behind it.
            setWarningOpen(false);
            requestChange(false);
          }}
          onCancel={() => setWarningOpen(false)}
        />
      )}
    </>
  );
};

export default SchoolMfaPanel;
